import { useEffect, useState } from 'react';
import { Box, FormControl, FormLabel, Select, VStack } from '@chakra-ui/react';

import StrongModal from '../common/StrongModal';
import StrongInput from '../common/StrongInput';
import StrongImageDragDrop from '../common/StrongImageDragDrop';
import StrongTextarea from '../common/StrongTextarea';
import StrongSelect from '../common/StrongSelect';

const getRelatedExerciseId = (exercise: string | ExerciseTitle) => {
  if (typeof exercise === 'string') return exercise;
  return exercise.id || exercise._id || '';
};

const getSelectedRelatedExerciseIds = (exercise?: Exercise) => {
  const relatedExercises =
    exercise?.relatedExercises ||
    exercise?.alternativeExercises ||
    exercise?.relatedExerciseIds ||
    exercise?.relatedExercise ||
    exercise?.related_exercises ||
    exercise?.related_exercise_ids ||
    exercise?.labeledExercises ||
    exercise?.labelledExercises ||
    [];

  return (Array.isArray(relatedExercises) ? relatedExercises : [relatedExercises])
    .map(getRelatedExerciseId)
    .filter(Boolean);
};

interface EditExerciseModalProps {
  selectedExercise?: Exercise;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    title: string,
    description: string,
    thumbnail: File | null,
    videoId: string,
    selectedcategories: string[],
    selectedequipments: string[],
    genders: string[],
    type: string,
    selectedRelatedExercises: string[],
  ) => void;
  equipmentstitle: EquipmentTitle[];
  categoriestitle: CategoryTitle[];
  exercisestitle: ExerciseTitle[];
}

const EditExerciseModal = ({
  selectedExercise,
  isOpen,
  onClose,
  onSubmit,
  equipmentstitle,
  categoriestitle,
  exercisestitle,
}: EditExerciseModalProps) => {
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [videoId, setVideoId] = useState<string>('');
  const [selectedcategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedequipments, setSelectedEquipments] = useState<string[]>([]);
  const [selectedRelatedExercises, setSelectedRelatedExercises] = useState<string[]>([]);

  const [genders, setGenders] = useState<string[]>([]);                    
  const [type, setType] = useState<string>('exercise');                    
  
  useEffect(() => {
    setTitle(selectedExercise?.title || '');
    setDescription(selectedExercise?.description || '');
    setThumbnail(null);
    setVideoId(selectedExercise?.videoId || '');
    setSelectedCategories(selectedExercise?.categories || []);
    setSelectedEquipments(selectedExercise?.equipments || []);
    setSelectedRelatedExercises(getSelectedRelatedExerciseIds(selectedExercise));
    setGenders(selectedExercise?.genders?.length ? selectedExercise.genders : (selectedExercise?.gender ? [selectedExercise.gender] : []));
    setType(selectedExercise?.type || 'exercise');
  }, [selectedExercise,isOpen]);

  const relatedExerciseOptions = exercisestitle
    ?.filter(({ id }) => id !== selectedExercise?._id)
    .map(({ title, id }) => ({ label: title, value: id })) || [];

  return (
    <StrongModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={() =>
        onSubmit(
          title,
          description,
          thumbnail,
          videoId,
          selectedcategories,
          selectedequipments,
          genders,
          type,
          selectedRelatedExercises
        )
      }
      title={selectedExercise ? 'Edit Exercise' : 'Create Exercise'}
      isSubmitDisabled={!title}
      body={
        <VStack spacing={4} align='stretch'>
          <FormControl>
            <FormLabel color='gray.600'>Title</FormLabel>
            <StrongInput
              placeholder='Enter title'
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </FormControl>

          <FormControl>
            <FormLabel color='gray.600'>Description</FormLabel>
            <StrongTextarea
              placeholder='Enter description'
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </FormControl>

          <FormControl>
            <FormLabel color='gray.600'>Thumbnail</FormLabel>
            <StrongImageDragDrop
              onFileSelect={setThumbnail}
              defaultImageUrl={selectedExercise?.thumbnail}
            />
          </FormControl>
          <FormControl>
            <FormLabel color='gray.600'>Vimeo ID</FormLabel>
            <StrongInput
              placeholder='Paste Vimeo ID'
              value={videoId}
              onChange={(e) => setVideoId(e.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel color='gray.600'>Gender</FormLabel>
            <StrongSelect
              options={[{ label: 'Male', value: 'male' }, { label: 'Female', value: 'female' }]}
              value={genders.map((g) => ({ label: g[0].toUpperCase() + g.slice(1), value: g }))}
              onSelectChange={setGenders}
            />
          </FormControl>

          <FormControl>   
            <FormLabel color='gray.600'>Type</FormLabel>                         
            <Select                                       value={type}                                                       
              onChange={(e) => setType(e.target.value)}
            >                                            
              <option value='exercise'>Exercise</option>
              <option value='warmup'>Warmup</option>                                   
            </Select>                     
          </FormControl> 
          
          <FormControl>
            <FormLabel color='gray.600'>Categories</FormLabel>
            <StrongSelect
              options={categoriestitle?.map(({ title, id }) => ({ label: title, value: id })) || []}
              value={selectedcategories.map((value) => {
                const category_title = categoriestitle?.find((categorytitle) => categorytitle.id === value) as SectionTitle;
                return { label: category_title.title, value: category_title.id };
              })}
              onSelectChange={setSelectedCategories}
            />
          </FormControl>
          <FormControl>
            <FormLabel color='gray.600'>Equipment</FormLabel>
            <StrongSelect
              options={equipmentstitle?.map(({ title, id }) => ({ label: title, value: id })) || []}
              value={selectedequipments.map((value) => {
                const equipment_title = equipmentstitle?.find((equipmenttile) => equipmenttile.id === value) as SectionTitle;
                return { label: equipment_title.title, value: equipment_title.id };
              })}
              onSelectChange={setSelectedEquipments}
            />
          </FormControl>
          <FormControl>
            <FormLabel color='gray.600'>Related Exercises</FormLabel>
            <StrongSelect
              options={relatedExerciseOptions}
              value={selectedRelatedExercises.map((value) => {
                const exerciseTitle = exercisestitle?.find((exercise) => exercise.id === value);
                return {
                  label: exerciseTitle?.title || 'Unknown Exercise',
                  value,
                };
              })}
              onSelectChange={setSelectedRelatedExercises}
            />
          </FormControl>
          <FormControl>
            <FormLabel color='gray.600'>Appears in programs</FormLabel>
            {selectedExercise?.appearsInPrograms?.length ? (
              selectedExercise.appearsInPrograms.map((program) => (
                <Box
                  key={`${program.pid}-${program.title}`}
                  p={3}
                  bg='gray.50'
                  borderRadius='md'
                  borderWidth='1px'
                  borderColor='gray.200'
                >
                  <Box fontWeight='semibold'>{program.title}</Box>
                  <Box fontSize='sm' color='gray.500'>PID: {program.pid}</Box>
                </Box>
              ))
            ) : (
              <Box p={3} bg='gray.50' borderRadius='md' borderWidth='1px' borderColor='gray.200'>
                No programs yet
              </Box>
            )}
          </FormControl>
        </VStack>
      }
    />
  );
};

export default EditExerciseModal;
